/**
 * TransferSummary - Transfer fee summary component
 */
import { motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';

const TransferSummary = ({ 
  amount, 
  currency, 
  transferMethod, 
  bankCountry,
  currentBalance 
}) => {
  const calculateFee = () => {
    const amt = parseFloat(amount) || 0;
    if (transferMethod === 'bank') {
      const bankFee = amt * 0.025;
      const flatFee = bankCountry === 'US' ? 1.0 : 5.0;
      return (bankFee + flatFee).toFixed(2);
    }
    return (amt * 0.015).toFixed(2);
  };

  const getTotalDeduction = () => {
    const amt = parseFloat(amount) || 0;
    const fee = parseFloat(calculateFee());
    return (amt + fee).toFixed(2);
  };

  const getFeeLabel = () => {
    if (transferMethod === 'bank') {
      return bankCountry === 'US' ? '2.5% + $1' : '2.5% + $5';
    }
    return '1.5%';
  };

  const totalDeduction = parseFloat(getTotalDeduction());
  const hasInsufficientFunds = totalDeduction > currentBalance;

  if (!amount || parseFloat(amount) <= 0) {
    return null;
  }

  return (
    <>
      <motion.div 
        className="bg-muted rounded-xl p-4 space-y-3"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Send Amount</span>
          <span className="font-mono">${parseFloat(amount).toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Transfer Fee ({getFeeLabel()})</span>
          <span className="font-mono text-orange-500">+${calculateFee()}</span>
        </div>
        <div className="border-t border-border pt-3 flex justify-between">
          <span className="font-medium">Total Deduction</span>
          <span className="font-mono font-bold" data-testid="total-deduction">
            ${getTotalDeduction()} {currency}
          </span>
        </div>
      </motion.div>

      {/* Insufficient Funds Warning */}
      {hasInsufficientFunds && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-destructive/10 text-destructive">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm">Insufficient funds for this transfer</span>
        </div>
      )}
    </>
  );
};

export default TransferSummary;
